// Global Types
import type {
  $WithRouter,
} from '@vroskus/library-navigation';
import type {
  $Model,
} from 'common.data';
import type {
  $Translate,
} from 'common.language';

// Global Enums
import {
  adminPages,
  adminSections,
} from 'common.enums';

// Global Components
import * as React from 'react';
import {
  Button,
  Card,
  Table,
} from 'reactstrap';
import {
  DeleteDialog,
  FormDialog,
  Loader,
  SectionHeader,
} from '@vroskus/library-components';

// Helpers
import {
  cleanFormValues,
} from '@vroskus/library-helpers/dist/browser';
import {
  defaultDeleteItem,
  defaultGetData,
  defaultUpsertItem,
} from '../../../helpers';

// Components
import Page from '../../layout/Page';
import EmptyTable from '../../common/EmptyTable';
import CrewmemberSocialUserFields from '../../fields/CrewmemberSocialUserFields';
import ThQty from '../../common/ThQty';

// Services
import Services from '../../../services';

// Types
import type {
  $RedirectAction,
} from '../../../redux/modules/main/actions';

export type $OwnProps = $Translate & $WithRouter;

export type $StateProps = Record<string, never>;

export type $DispatchProps = {
  redirect: $RedirectAction;
};

type $Props = $DispatchProps & $OwnProps & $StateProps;

type $State = {
  data: Array<$Model<'CrewmemberSocialUser'>>;
  loading: boolean;
  mainItem: $Model<'Crewmember'> | null;
};

const pageId = adminPages.adminCrewmemberSocialUsersPage;

class AdminCrewmemberSocialUsersPageView extends React.Component<$Props, $State> {
  constructor(props: $Props) {
    super(props);

    this.state = {
      data: [],
      loading: false,
      mainItem: null,
    };
  }

  componentDidMount(): void {
    this.getData();
    this.setupSocket();
  }

  setupSocket(): void {
    Services.Socket.on({
      action: (data) => {
        if (data.modelName === 'CrewmemberSocialUser') {
          this.getData();
        }
      },
      type: Services.Socket.type.modelChangedEvent,
    });
  }

  setStateAsync(state: Partial<$State>): Promise<void> {
    return new Promise((resolve) => {
      this.setState(
        (pervState: $State) => ({
          ...pervState,
          ...state,
        }),
        resolve,
      );
    });
  }

  async getData(): Promise<void> {
    const {
      params,
    } = this.props;

    const {
      CrewmemberId,
    } = params;

    await defaultGetData({
      action: async () => {
        if (CrewmemberId) {
          await this.setStateAsync({
            loading: true,
          });

          const mainItem = await Services.Api.getOneAdminCrewmember({
            id: CrewmemberId,
          });
          const data = await Services.Api.getManyAdminCrewmemberSocialUser({
            CrewmemberId,
          });

          await this.setStateAsync({
            data,
            loading: false,
            mainItem,
          });
        }
      },
      errorAction: async () => this.setStateAsync({
        loading: false,
      }),
    });
  }

  renderHeader() {
    const {
      redirect,
      t,
    } = this.props;
    const {
      mainItem,
    } = this.state;

    return (
      <Card id={adminSections[pageId].header}>
        <SectionHeader
          description={mainItem !== null ? mainItem.Name : ''}
          title={t('Crewmember social users')}
          titleButton={(
            <Button
              className={'mr-2'}
              color={'secondary'}
              id={'redirect-admin-crewmembers-page-button'}
              onClick={() => redirect({
                pathname: '/admin/crewmembers',
              })}
              size={'sm'}
            >
              <em className={'fa icon-arrow-left'} />
            </Button>
          )}
        />
      </Card>
    );
  }

  renderButtons(dataItem: $Model<'CrewmemberSocialUser'>) {
    const {
      t,
    } = this.props;

    const {
      id,
    } = dataItem;

    return (
      <div className={'d-flex justify-content-end'}>
        <FormDialog
          fields={(
            <CrewmemberSocialUserFields item={dataItem} />
          )}
          id={`update-crewmember-social-user-dialog-${id}`}
          labelCloseButton={t('Close')}
          labelSaveButton={t('Save')}
          onAction={(values) => defaultUpsertItem({
            action: async () => {
              const params = cleanFormValues<$Model<'CrewmemberSocialUser'>>({
                ...dataItem,
                ...values,
              });

              await Services.Api.updateAdminCrewmemberSocialUser(params);
            },
          })}
          subTitle={dataItem.Name}
          title={t('Crewmember social user')}
        >
          <Button
            className={'mr-1'}
            color={'warning'}
            id={`update-crewmember-social-user-button-${id}`}
            outline
            size={'sm'}
          >
            {t('Edit')}
          </Button>
        </FormDialog>
        <DeleteDialog
          content={t('Do you really want to delete?')}
          id={`delete-crewmember-social-user-dialog-${id}`}
          labelCloseButton={t('Close')}
          labelDeleteButton={t('Delete')}
          onAction={() => defaultDeleteItem({
            action: async () => Services.Api.deleteAdminCrewmemberSocialUser(dataItem),
          })}
          subTitle={dataItem.Name}
          title={t('Crewmember social user')}
        >
          <Button
            color={'danger'}
            id={`delete-crewmember-social-user-button-${id}`}
            outline
            size={'sm'}
          >
            {t('Delete')}
          </Button>
        </DeleteDialog>
      </div>
    );
  }

  renderTable() {
    const {
      t,
    } = this.props;
    const {
      data,
    } = this.state;

    return data.length === 0 ? (
      <EmptyTable className={'mt-5'} />
    ) : (
      <Table responsive>
        <thead>
          <tr>
            <th>{t('Name')}</th>
            <th>{t('Email')}</th>
            <th>{t('Type')}</th>
            <th>{t('Uid')}</th>
            <ThQty value={data.length} />
          </tr>
        </thead>
        <tbody>
          {data.map((dataItem: $Model<'CrewmemberSocialUser'>) => (
            <tr key={dataItem.id}>
              <td>{dataItem.Name}</td>
              <td>{dataItem.Email}</td>
              <td>{dataItem.Type}</td>
              <td>{dataItem.Uid}</td>
              <td>
                {this.renderButtons(dataItem)}
              </td>
            </tr>
          ))}
        </tbody>
      </Table>
    );
  }

  renderContent() {
    const {
      loading,
    } = this.state;

    return (
      <Card id={adminSections[pageId].list}>
        <Loader
          loading={loading === true}
          overlay
        >
          {this.renderTable()}
        </Loader>
      </Card>
    );
  }

  render() {
    return (
      <Page id={pageId}>
        <>
          {this.renderHeader()}
          {this.renderContent()}
        </>
      </Page>
    );
  }
}

export default AdminCrewmemberSocialUsersPageView;
